import React from 'react';
import { createPortal } from 'react-dom';

const SubscriptionDetails = ({ isOpen, onClose, subscription }) => {
  if (!isOpen || !subscription) return null;

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';

    let date;
    
    // Handle ISO date strings (like "2026-02-04T17:20:17.000Z")
    if (typeof timestamp === 'string') {
      if (timestamp.includes('T') || timestamp.includes('-')) {
        // ISO date string
        date = new Date(timestamp);
      } else if (!isNaN(timestamp)) {
        // Numeric string (milliseconds)
        date = new Date(parseInt(timestamp));
      } else {
        date = new Date(timestamp);
      }
    } else if (typeof timestamp === 'number') {
      date = new Date(timestamp);
    } else {
      date = new Date(timestamp);
    }

    if (isNaN(date.getTime())) return 'Invalid Date';

    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDaysRemaining = (expiresAt) => {
    if (!expiresAt) return 0;

    const now = new Date();
    let expiry;

    // Handle ISO date strings (like "2026-04-05T17:20:17.000Z")
    if (typeof expiresAt === 'string') {
      if (expiresAt.includes('T') || expiresAt.includes('-')) {
        // ISO date string
        expiry = new Date(expiresAt);
      } else if (!isNaN(expiresAt)) {
        // Numeric string (milliseconds)
        expiry = new Date(parseInt(expiresAt));
      } else {
        expiry = new Date(expiresAt);
      }
    } else if (typeof expiresAt === 'number') {
      expiry = new Date(expiresAt);
    } else {
      expiry = new Date(expiresAt);
    }

    if (isNaN(expiry.getTime())) return 0;

    const diffTime = expiry - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  const getStatusColor = (status) => {
    const statusColors = {
      active: 'bg-green-100 text-green-800',
      expired: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800',
      suspended: 'bg-yellow-100 text-yellow-800'
    };
    return statusColors[status] || 'bg-gray-100 text-gray-800';
  };

  return createPortal(
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[999999] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden animate-in zoom-in duration-300">
        {/* Header */}
        <div className="bg-gradient-to-r from-bulky-navy-800 to-bulky-navy-950 px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center p-1">
              <img
                src="/bm-logo.png"
                alt="Bulky Marketing Logo"
                className="max-w-full max-h-full object-contain"
              />
            </div>
            <div>
              <h2 className="font-medium">Subscription Details</h2>
              <p className="text-blue-100 text-sm">ID: #{subscription.id}</p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-all"
          >
            <span className="material-icons-round text-lg">close</span>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-100px)]">
          {/* User Information */}
          <div className="bg-blue-50 rounded-xl p-4 mb-6">
            <h2 className="font-medium">
              <span className="material-icons-round text-blue-600">person</span>
              User Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600 block">Username</span>
                <span className="font-normal">{subscription.user?.username || 'N/A'}</span>
              </div>
              <div>
                <span className="text-gray-600 block">Email</span>
                <span className="font-normal">{subscription.user?.email || 'N/A'}</span>
              </div>
              <div>
                <span className="text-gray-600 block">Role</span>
                <span className="font-normal capitalize">{subscription.user?.role?.replace('_', ' ') || 'N/A'}</span>
              </div>
              <div>
                <span className="text-gray-600 block">User ID</span>
                <span className="font-normal">#{subscription.user?.id || 'N/A'}</span>
              </div>
            </div>
          </div>

          {/* Package Information */}
          <div className="bg-green-50 rounded-xl p-4 mb-6">
            <h2 className="font-medium">
              <span className="material-icons-round text-green-600">inventory_2</span>
              Package Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600 block">Package Name</span>
                <span className="font-normal text-lg">{subscription.package?.name || 'N/A'}</span>
              </div>
              <div>
                <span className="text-gray-600 block">Price</span>
                <span className="font-normal text-lg">₹{subscription.package?.price || 'N/A'}</span>
              </div>
              <div>
                <span className="text-gray-600 block">Currency</span>
                <span className="font-normal">{subscription.package?.currency || 'INR'}</span>
              </div>
              <div>
                <span className="text-gray-600 block">Package ID</span>
                <span className="font-normal">#{subscription.package?.id || 'N/A'}</span>
              </div>
            </div>
          </div>

          {/* Subscription Status */}
          <div className="bg-blue-50 rounded-xl p-4 mb-6">
            <h2 className="font-medium">
              <span className="material-icons-round text-blue-600">schedule</span>
              Subscription Status
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600 block">Status</span>
                <span className={`inline-block px-2 py-1 rounded-full text-xs font-normal ${getStatusColor(subscription.status)}`}>
                  {subscription.status?.charAt(0).toUpperCase() + subscription.status?.slice(1)}
                </span>
              </div>
              <div>
                <span className="text-gray-600 block">Start Date</span>
                <span className="font-normal">{formatDate(subscription.starts_at)}</span>
              </div>
              <div>
                <span className="text-gray-600 block">End Date</span>
                <span className="font-normal">{formatDate(subscription.expires_at)}</span>
              </div>
              {subscription.status === 'active' && (
                <div>
                  <span className="text-gray-600 block">Days Remaining</span>
                  <span className="font-normal text-green-600">{getDaysRemaining(subscription.expires_at)} days</span>
                </div>
              )}
            </div>
          </div>

          {/* Order Information */}
          {subscription.order && (
            <div className="bg-yellow-50 rounded-xl p-4">
              <h2 className="font-medium">
                <span className="material-icons-round text-yellow-600">receipt</span>
                Order Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600 block">Order ID</span>
                  <span className="font-normal">#{subscription.order.id}</span>
                </div>
                <div>
                  <span className="text-gray-600 block">Amount Paid</span>
                  <span className="font-normal">₹{subscription.order.amount}</span>
                </div>
                <div>
                  <span className="text-gray-600 block">Payment Status</span>
                  <span className={`inline-block px-2 py-1 rounded-full text-xs font-normal ${getStatusColor(subscription.order.status)}`}>
                    {subscription.order.status?.charAt(0).toUpperCase() + subscription.order.status?.slice(1)}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600 block">Order Date</span>
                  <span className="font-normal">{formatDate(subscription.order.created_at)}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-4">
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg font-normal transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SubscriptionDetails;